import type { ReactNode } from 'react';
import { useFormat, type Formatter } from '@paige/format';
import type { AccountSummary } from './account-types';
import { reasonWords } from './reason-words';
import './insights.css';
import './account.css';

interface Item {
  key: string;
  count: number;
  title: string;
  detail: string;
  href: string;
  /** Held, failing or down: a card of its own. Everything else is a count in the line. */
  critical: boolean;
}

/** "Customer contracts, paused by Dana 5 hr. ago" or "…, paused by Paige: the destination is not answering". */
function pausedWords(a: AccountSummary, f: Formatter): string {
  const p = a.rightNow.paused;
  if (!p) return `${a.setup.jobsPaused} paused`;
  const when = p.at ? ` ${f.ago(p.at)}` : '';
  const who = p.reason ? `by Paige: ${reasonWords(p.reason)}` : `by ${p.by ?? 'a person'}${when}`;
  return `${p.job}, paused ${who}${a.setup.jobsPaused > 1 ? `, and ${a.setup.jobsPaused - 1} more` : ''}`;
}

type Row = [key: string, count: number, title: string, detail: string, href: string];

const itemsOf = (rows: Row[], critical: boolean): Item[] =>
  rows.map(([key, count, title, detail, href]) => ({ key, count, title, detail, href, critical }));

/** Held, stuck and failing: what stops the company's work. */
function blockingItems(a: AccountSummary, links: Links): Item[] {
  const r = a.rightNow;
  const failedTo = r.exportsFailedDestination ? `mostly to ${r.exportsFailedDestination}` : '';
  return itemsOf(
    [
      ['held', r.heldUnits, 'held for pages', 'waiting for the refill or a pack', '#ad-money'],
      ['stuck', r.stuck, 'stuck', `mostly ${reasonWords(r.stuckReason)}`, links.problems],
      ['exports', r.exportsFailed24h, 'exports failed in 24 h', failedTo, links.queues],
    ],
    true,
  );
}

/** Waiting for a person: exceptions and review. */
function waitingItems(a: AccountSummary, links: Links, f: Formatter): Item[] {
  const r = a.rightNow;
  const oldest = r.oldestReviewAt ? `oldest ${f.ago(r.oldestReviewAt)}` : '';
  const exceptions = r.exceptions === 1 ? 'exception' : 'exceptions';
  return itemsOf(
    [
      [
        'exceptions',
        r.exceptions,
        exceptions,
        `mostly ${reasonWords(r.exceptionsReason)}`,
        links.queues,
      ],
      ['review', r.waitingReview, 'waiting in review', oldest, links.queues],
    ],
    false,
  );
}

function setupItems(a: AccountSummary, f: Formatter): Item[] {
  return [
    {
      key: 'sources',
      count: a.setup.sourcesDown,
      title: 'intake down',
      detail: 'email, folder or scanner sources not answering',
      href: '#ad-devices',
      critical: true,
    },
    {
      key: 'mail',
      count: a.rightNow.mailRefused7d,
      title: 'mail refused in 7 days',
      detail: 'senders not on the allowed list',
      href: '#ad-details',
      critical: false,
    },
    {
      key: 'paused',
      count: a.setup.jobsPaused,
      title: a.setup.jobsPaused === 1 ? 'job paused' : 'jobs paused',
      detail: pausedWords(a, f),
      href: '#ad-details',
      critical: false,
    },
  ];
}

interface Links {
  problems: string;
  queues: string;
}

export interface RightNowProps {
  account: AccountSummary;
  /** The company's slug, for the links into Operations, filtered to it. */
  company: string;
  /** When the account was read, for "checked 14:22". */
  checkedAt: number;
}

/**
 * What is wrong for the company right now (round 2, support R2-M1, dataviz N3): above the signals,
 * one line of counts with their reasons in support's words when nothing is critical, and a card only
 * for what is held, failing or down. Every link lands on this company.
 */
export function RightNow({ account, company, checkedAt }: RightNowProps): ReactNode {
  const f = useFormat();
  const q = encodeURIComponent(company);
  const links = { problems: `/admin/problems?tenant=${q}`, queues: `/admin/queues?tenant=${q}` };
  const items = [
    ...blockingItems(account, links),
    ...waitingItems(account, links, f),
    ...setupItems(account, f),
  ].filter((i) => i.count > 0);
  const checked = `checked ${f.time(new Date(checkedAt).toISOString())}`;
  // Every file the company sent, newest first, each opening its timeline: no id needed.
  const arrivals = (
    <a className="ad-now__arrivals" href={`/admin/timeline?company=${q}`}>
      See what arrived
    </a>
  );
  if (items.length === 0) {
    return (
      <p className="ad-now ad-now--quiet" id="ad-right-now">
        <span aria-hidden="true">✓</span> Right now: nothing stuck, failing or held; {checked}.{' '}
        {arrivals}
      </p>
    );
  }
  return (
    <section className="ad-now" id="ad-right-now" aria-label="Right now">
      <h2 className="ad-now__title">
        Right now <span className="ad-small">{checked}</span>{' '}
        <span className="ad-small">{arrivals}</span>
      </h2>
      <Cards items={items.filter((i) => i.critical)} />
      <Line items={items.filter((i) => !i.critical)} />
    </section>
  );
}

function Cards({ items }: { items: readonly Item[] }): ReactNode {
  const f = useFormat();
  if (items.length === 0) return null;
  return (
    <ul className="ad-now__cards">
      {items.map((c) => (
        <li key={c.key}>
          <a href={c.href}>
            <strong>{f.int(c.count)}</strong> {c.title}
            <span className="ad-small">{c.detail}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function Line({ items }: { items: readonly Item[] }): ReactNode {
  const f = useFormat();
  if (items.length === 0) return null;
  return (
    <ul className="ad-now__line">
      {items.map((c) => (
        <li key={c.key}>
          <a href={c.href}>
            <strong>{f.int(c.count)}</strong> {c.title}
          </a>
          {c.detail ? <span className="ad-small">: {c.detail}</span> : null}
        </li>
      ))}
    </ul>
  );
}
